import { zodResolver } from '@hookform/resolvers/zod';

import type { AssignedAgency } from '../../../../auth/agencyAccess';
import { dashboardT } from '../../../../../lib/messages';
import type { Brand, BrandPayload } from '../../model';
import { brandDialogSchema, type BrandDialogFormValue } from './BrandDialog.schema';

export function brandDialogDefaultValues(
  brand: Brand | null,
  agency: AssignedAgency | null | undefined,
): BrandDialogFormValue {
  return {
    agencyId: agency?.id ?? '',
    name: brand?.name ?? '',
    industry: brand?.industry ?? '',
    details: brand?.details ?? '',
    url: brand?.url ?? '',
    countryCodes: [...(brand?.countryCodes ?? [])],
    logo: undefined,
    removeLogo: false,
  };
}

/** Form schema resolver; requires an agency when the dialog prepares the brand for one. */
export function brandDialogResolver(prepareAgency: boolean) {
  return zodResolver(
    brandDialogSchema.superRefine((values, context) => {
      if (prepareAgency && !values.agencyId)
        context.addIssue({
          code: 'custom',
          path: ['agencyId'],
          message: dashboardT('agencyAssignment.required'),
        });
    }),
  );
}

/** Request payload; `removeLogo` is only sent when clearing an existing brand's logo. */
export function buildBrandPayload(values: BrandDialogFormValue, brand: Brand | null): BrandPayload {
  return {
    name: values.name,
    industry: values.industry,
    details: values.details,
    url: values.url,
    countryCodes: values.countryCodes,
    ...(brand && values.removeLogo ? { removeLogo: true } : {}),
  };
}

/** Logo mutation input: omitted when untouched, `null` when cleared, the file when replaced. */
export function brandLogoInput(logo: File | null | undefined): { logo?: File | null } {
  return logo === undefined ? {} : { logo };
}
